import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useUser } from '../../context/UserContext';
import {
  Building2,
  Search,
  ChevronDown,
  Lock,
  Clock,
  CheckCircle,
  ArrowRight,
  Layers,
  MapPin,
  Sparkles,
} from 'lucide-react';
import axiosInstance from '../../services/api/axios';
import { toast } from 'react-toastify';
import usePagination from '../../hooks/usePagination';
import PaginationControls from '../../components/PaginationControls';
import { getMediaUrl, mapMediaItems } from '../../utils/media';
import { useNavigate } from 'react-router-dom';
import LoadingImage from '../../components/LoadingImage';

/** Same address line as BV phase list cards */
function formatProjectAddress(project) {
  if (!project?.address) return 'Address not available';
  const a = project.address;
  return `${a.buildingNo} ${a.streetName1}${
    a.streetName2 ? ', ' + a.streetName2 : ''
  }, ${a.district}, ${a.city}, ${a.state} ${a.pinCode}`;
}

const Phases = () => {
  const { userInfo } = useUser();
  const navigate = useNavigate();
  const [mediaImages, setMediaImages] = useState({});
  const fetchingMediaRef = useRef(new Set());
  const [cities, setCities] = useState([]);
  const [types, setTypes] = useState([]);
  const [selectedCity, setSelectedCity] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedSubscription, setSelectedSubscription] = useState('all');
  const [sortBy, setSortBy] = useState('highest_completion');
  const [subscriptionStatuses, setSubscriptionStatuses] = useState({});

  const fetchPhaseImage = useCallback(async (phaseId) => {
    if (!phaseId || fetchingMediaRef.current.has(phaseId)) return;
    fetchingMediaRef.current.add(phaseId);
    try {
      const response = await axiosInstance.get('/projects/phase/media', {
        params: { projectPhaseId: phaseId, images: true, progress: false, guidelines: false, boq: false },
      });
      const mediaArray = response?.items || (Array.isArray(response) ? response : []);
      const [mediaData] = mapMediaItems(mediaArray, 'Phase Image');
      setMediaImages((prev) => {
        if (prev[phaseId]) return prev;
        return { ...prev, [phaseId]: mediaData || null };
      });
    } catch (error) {
      console.error('Error fetching phase image:', error);
      setMediaImages((prev) => ({ ...prev, [phaseId]: null }));
    } finally {
      fetchingMediaRef.current.delete(phaseId);
    }
  }, []);

  const fetchPhases = useCallback(
    async ({ page, page_size, search }) => {
      try {
        const params = { page, page_size, search: search || undefined };
        const response = await axiosInstance.get('/interior-designers/project-phases/search', { params });

        let phasesData = [];
        let paginationInfo = {};

        if (response?.items && response.total !== undefined) {
          phasesData = response.items;
          paginationInfo = {
            total: response.total,
            page: response.page || page,
            page_size: response.page_size || page_size,
            total_pages: response.total_pages || Math.ceil(response.total / (response.page_size || page_size)),
            has_next:
              response.has_next !== undefined
                ? response.has_next
                : (response.page || page) < (response.total_pages || Math.ceil(response.total / (response.page_size || page_size))),
            has_previous: response.has_previous !== undefined ? response.has_previous : (response.page || page) > 1,
          };
        } else if (Array.isArray(response)) {
          const startIndex = (page - 1) * page_size;
          const endIndex = startIndex + page_size;
          phasesData = response.slice(startIndex, endIndex);
          paginationInfo = {
            total: response.length,
            page,
            page_size,
            total_pages: Math.ceil(response.length / page_size),
            has_next: endIndex < response.length,
            has_previous: page > 1,
          };
        } else {
          return { items: [], total: 0, page, page_size, total_pages: 0, has_next: false, has_previous: false };
        }

        phasesData.forEach((phaseData) => {
          const phaseId = phaseData.projectPhase?.projectPhaseId || phaseData.projectPhaseId;
          if (phaseId && !fetchingMediaRef.current.has(phaseId)) {
            fetchPhaseImage(phaseId);
          }
        });

        return { items: phasesData, ...paginationInfo };
      } catch (error) {
        console.error('Error fetching phases:', error);
        toast.error('Failed to fetch phases');
        return { items: [], total: 0, page: 1, page_size: 12, total_pages: 0, has_next: false, has_previous: false };
      }
    },
    [fetchPhaseImage]
  );

  const {
    data: phases,
    pagination,
    searchInput,
    loading,
    goToPage,
    changePageSize,
    handleSearchInputChange,
    triggerSearch,
  } = usePagination(fetchPhases, 12, '', true, true);

  useEffect(() => {
    if (phases && phases.length > 0) {
      const uniqueCities = new Set();
      const uniqueTypes = new Set();
      phases.forEach((phaseData) => {
        const project = phaseData.projectPhase?.project || {};
        if (project.address?.city) uniqueCities.add(project.address.city);
        if (project.type) uniqueTypes.add(project.type);
      });
      setCities(Array.from(uniqueCities).sort());
      setTypes(Array.from(uniqueTypes).sort());
    }
  }, [phases]);

  const filteredAndSortedPhases = useMemo(() => {
    if (!phases) return [];
    let filtered = phases.filter((phaseData) => {
      const project = phaseData.projectPhase?.project || {};
      const city = project.address?.city || '';
      const type = project.type || '';
      const phaseId = phaseData.projectPhase?.projectPhaseId;
      const subscriptionStatus = phaseId ? subscriptionStatuses[phaseId] || 'none' : 'none';
      const matchesCity = selectedCity === 'all' || city === selectedCity;
      const matchesType = selectedType === 'all' || type === selectedType;
      const matchesSubscription =
        selectedSubscription === 'all' ||
        (selectedSubscription === 'subscribed' && subscriptionStatus === 'paid') ||
        (selectedSubscription === 'not_subscribed' && subscriptionStatus === 'none');
      return matchesCity && matchesType && matchesSubscription;
    });
    if (sortBy === 'highest_completion') {
      filtered.sort((a, b) => (b.completionPercentage ?? 0) - (a.completionPercentage ?? 0));
    } else if (sortBy === 'lowest_completion') {
      filtered.sort((a, b) => (a.completionPercentage ?? 0) - (b.completionPercentage ?? 0));
    }
    return filtered;
  }, [phases, selectedCity, selectedType, selectedSubscription, sortBy, subscriptionStatuses]);

  // Aggregate KPI counts driven by current page (matches what's shown).
  const kpiCounts = useMemo(() => {
    const list = phases || [];
    let unlocked = 0;
    let pending = 0;
    let locked = 0;
    list.forEach((p) => {
      const id = p.projectPhase?.projectPhaseId;
      const s = id ? subscriptionStatuses[id] || 'none' : 'none';
      if (s === 'paid') unlocked += 1;
      else if (s === 'pending') pending += 1;
      else locked += 1;
    });
    return { unlocked, pending, locked };
  }, [phases, subscriptionStatuses]);

  useEffect(() => {
    const fetchSubscriptionStatuses = async () => {
      if (!phases || phases.length === 0 || !userInfo?.loginId) return;
      try {
        const response = await axiosInstance.get(`/interior-designers/phase-subscriptions`);
        let subscriptions = [];
        if (response?.items && Array.isArray(response.items)) subscriptions = response.items;
        else if (Array.isArray(response)) subscriptions = response;
        const statusMap = {};
        phases.forEach((phaseData) => {
          const phaseId = phaseData.projectPhase?.projectPhaseId;
          if (phaseId) {
            const subscription = subscriptions.find(
              (sub) => sub.projectPhase?.projectPhaseId == phaseId || sub.projectPhaseId == phaseId
            );
            if (subscription) {
              const paymentStatus = subscription.paymentStatus?.toUpperCase();
              statusMap[phaseId] =
                paymentStatus === 'PAID' ? 'paid' : paymentStatus === 'PENDING' ? 'pending' : 'none';
            } else {
              statusMap[phaseId] = 'none';
            }
          }
        });
        setSubscriptionStatuses(statusMap);
      } catch (error) {
        console.error('Error fetching subscription statuses:', error);
      }
    };
    fetchSubscriptionStatuses();
  }, [phases, userInfo]);

  const handlePhaseClick = (phaseData) => {
    const projectPhase = phaseData.projectPhase || {};
    const project = projectPhase.project || {};
    const projectId = project.projectId;
    const phaseId = projectPhase.projectPhaseId;
    if (projectId && phaseId) {
      navigate(`/interior-designer/phases/${projectId}/${phaseId}`, { state: { phaseData } });
    }
  };

  const handleSearchChange = (value) => {
    handleSearchInputChange(value);
    if (!value || value.trim() === '') triggerSearch();
  };

  const hasActiveFilters =
    selectedCity !== 'all' || selectedType !== 'all' || selectedSubscription !== 'all';

  const renderSubscriptionPill = (status) => {
    if (status === 'paid') {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/95 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm">
          <CheckCircle size={12} />
          Unlocked
        </span>
      );
    }
    if (status === 'pending') {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/95 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm">
          <Clock size={12} />
          Pending
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-slate-900/80 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm">
        <Lock size={12} />
        Locked
      </span>
    );
  };

  return (
    <div className="px-6 py-6 space-y-6">
      {/* Slate gradient hero band with KPI strip */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 to-slate-600 shadow-sm">
        <div className="absolute inset-0 opacity-[0.08]" style={{
          backgroundImage:
            'radial-gradient(circle at 20% 0%, rgba(255,255,255,0.6) 0, transparent 50%), radial-gradient(circle at 80% 100%, rgba(255,255,255,0.4) 0, transparent 50%)',
        }} />
        <div className="relative px-6 py-6 sm:px-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-white/80 backdrop-blur-sm">
                <Sparkles size={12} />
                Interior Designer
              </div>
              <h1 className="mt-3 text-2xl sm:text-3xl font-semibold text-white">Project Phases</h1>
              <p className="mt-1 text-sm text-white/70">
                Browse available phases, subscribe to unlock leads, and track work completion.
              </p>
            </div>
          </div>

          {/* KPI strip */}
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm ring-1 ring-white/10">
              <div className="flex items-center gap-2 text-white/70 text-xs">
                <Layers size={13} />
                Total phases
              </div>
              <div className="mt-1 text-2xl font-semibold text-white tabular-nums">
                {pagination?.total ?? 0}
              </div>
            </div>
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm ring-1 ring-white/10">
              <div className="flex items-center gap-2 text-emerald-200 text-xs">
                <CheckCircle size={13} />
                Unlocked
              </div>
              <div className="mt-1 text-2xl font-semibold text-white tabular-nums">{kpiCounts.unlocked}</div>
            </div>
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm ring-1 ring-white/10">
              <div className="flex items-center gap-2 text-amber-200 text-xs">
                <Clock size={13} />
                Pending
              </div>
              <div className="mt-1 text-2xl font-semibold text-white tabular-nums">{kpiCounts.pending}</div>
            </div>
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm ring-1 ring-white/10">
              <div className="flex items-center gap-2 text-white/70 text-xs">
                <Lock size={13} />
                Locked
              </div>
              <div className="mt-1 text-2xl font-semibold text-white tabular-nums">{kpiCounts.locked}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search by phase, project, RERA, city…"
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && triggerSearch()}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2 pl-10 pr-4 text-sm text-slate-800 placeholder-slate-400 outline-none transition-all focus:bg-white focus:border-flamingo-400 focus:ring-2 focus:ring-flamingo-200"
            />
          </div>

          <div className="grid grid-cols-2 gap-2 lg:flex lg:items-center lg:gap-2">
            <div className="relative">
              <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                className="appearance-none rounded-xl border border-slate-200 bg-white py-2 pl-3 pr-9 text-sm text-slate-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-flamingo-200 focus:border-flamingo-400 min-w-[140px]"
              >
                <option value="all">All cities</option>
                {cities.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
            </div>

            <div className="relative">
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="appearance-none rounded-xl border border-slate-200 bg-white py-2 pl-3 pr-9 text-sm text-slate-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-flamingo-200 focus:border-flamingo-400 min-w-[140px]"
              >
                <option value="all">All types</option>
                {types.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
            </div>

            <div className="relative">
              <select
                value={selectedSubscription}
                onChange={(e) => setSelectedSubscription(e.target.value)}
                className="appearance-none rounded-xl border border-slate-200 bg-white py-2 pl-3 pr-9 text-sm text-slate-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-flamingo-200 focus:border-flamingo-400 min-w-[170px]"
              >
                <option value="all">All subscriptions</option>
                <option value="subscribed">Subscribed</option>
                <option value="not_subscribed">Not subscribed</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
            </div>

            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="appearance-none rounded-xl border border-slate-200 bg-white py-2 pl-3 pr-9 text-sm text-slate-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-flamingo-200 focus:border-flamingo-400 min-w-[170px]"
              >
                <option value="highest_completion">Highest completion</option>
                <option value="lowest_completion">Lowest completion</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
            </div>
          </div>
        </div>

        {/* Result count + filter pill */}
        {!loading && (
          <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
            <p className="text-xs text-slate-600">
              Showing{' '}
              <span className="font-semibold text-slate-800">{filteredAndSortedPhases.length}</span>{' '}
              {filteredAndSortedPhases.length === 1 ? 'phase' : 'phases'}
              {hasActiveFilters && (
                <span className="text-slate-400"> · filtered from {pagination.total} total</span>
              )}
            </p>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={() => {
                  setSelectedCity('all');
                  setSelectedType('all');
                  setSelectedSubscription('all');
                }}
                className="text-xs font-medium text-flamingo-600 hover:text-flamingo-700"
              >
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Loading / empty / grid */}
      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center shadow-sm">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-flamingo-200 border-t-flamingo-500" />
          <p className="mt-3 text-sm text-slate-500">Loading phases…</p>
        </div>
      ) : filteredAndSortedPhases.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center shadow-sm">
          <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
            <Building2 className="text-slate-400" size={24} />
          </div>
          <p className="mt-4 text-base font-medium text-slate-800">No phases found</p>
          <p className="mt-1 text-sm text-slate-500">
            {searchInput
              ? 'Try adjusting your search terms or clearing filters.'
              : 'Phases will appear here when available.'}
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {filteredAndSortedPhases.map((phaseData) => {
              const projectPhase = phaseData.projectPhase || {};
              const project = projectPhase.project || {};
              const phaseId = projectPhase.projectPhaseId;
              const phaseName = projectPhase.name || 'Unnamed Phase';
              const completionPercent = phaseData.completionPercentage ?? 0;
              const subscriptionFee = phaseData.subscriptionFee ?? 0;
              const configCount = phaseData.configs?.length || 0;
              const subscriptionStatus = subscriptionStatuses[phaseId] || 'none';
              const imageUrl = mediaImages[phaseId] ? getMediaUrl(mediaImages[phaseId]) : null;
              const formattedAddress = formatProjectAddress(project);

              return (
                <div
                  key={phaseId}
                  onClick={() => handlePhaseClick(phaseData)}
                  className="group flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
                >
                  {/* Image header */}
                  <div className="relative">
                    {imageUrl ? (
                      <LoadingImage
                        src={imageUrl}
                        alt={phaseName}
                        containerClassName="h-40 w-full"
                        className="h-40 w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    ) : fetchingMediaRef.current.has(phaseId) ? (
                      <div className="flex h-40 w-full items-center justify-center bg-slate-100">
                        <div className="h-7 w-7 animate-spin rounded-full border-2 border-flamingo-200 border-t-flamingo-500" />
                      </div>
                    ) : (
                      <div className="flex h-40 w-full items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
                        <Building2 className="h-10 w-10 text-slate-300" />
                      </div>
                    )}
                    {/* Top overlay: subscription pill (left), completion (right) */}
                    <div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between p-2.5">
                      <div className="pointer-events-auto">{renderSubscriptionPill(subscriptionStatus)}</div>
                      <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-flamingo-500 to-flamingo-600 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm">
                        {(parseFloat(completionPercent) || 0).toFixed(2)}% complete
                      </span>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="flex flex-1 flex-col p-4">
                    <h3 className="truncate text-base font-semibold text-slate-900" title={phaseName}>
                      {phaseName}
                    </h3>
                    {project?.projectName && (
                      <p className="mt-0.5 truncate text-xs font-medium text-slate-600" title={project.projectName}>
                        {project.projectName}
                      </p>
                    )}

                    {/* Quick stats pills */}
                    {(subscriptionFee > 0 || configCount > 0) && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {subscriptionFee > 0 && (
                          <span className="inline-flex items-center rounded-full bg-flamingo-50 px-2.5 py-0.5 text-[11px] font-medium text-flamingo-700">
                            ₹{subscriptionFee.toLocaleString('en-IN')} fee
                          </span>
                        )}
                        {configCount > 0 && (
                          <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-700">
                            {configCount} config{configCount === 1 ? '' : 's'}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Address with icon */}
                    <div className="mt-3 flex items-start gap-1.5 text-xs text-slate-500">
                      <MapPin size={12} className="mt-0.5 shrink-0 text-slate-400" />
                      <span className="line-clamp-2" title={formattedAddress}>
                        {formattedAddress}
                      </span>
                    </div>

                    {project?.developer && (
                      <p className="mt-1 truncate text-[11px] text-slate-400" title={project.developer}>
                        by {project.developer}
                      </p>
                    )}

                    {projectPhase.reraNo && (
                      <p className="mt-1 text-[11px] text-slate-500">
                        <span className="font-medium text-slate-600">RERA:</span> {projectPhase.reraNo}
                      </p>
                    )}

                    {/* Spacer pushes CTA to the bottom */}
                    <div className="mt-auto pt-4">
                      <div className="flex items-center justify-between border-t border-slate-100 pt-3">
                        <span className="text-xs font-medium text-slate-500">View details</span>
                        <ArrowRight
                          size={14}
                          className="text-slate-400 transition-all group-hover:translate-x-0.5 group-hover:text-flamingo-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Inline pagination — bare row, no card chrome. Matches Leads. */}
          {pagination.total > 0 && (
            <PaginationControls
              pagination={pagination}
              onPageChange={goToPage}
              onPageSizeChange={changePageSize}
              itemName="phases"
              variant="list"
              showInfo={true}
            />
          )}
        </>
      )}
    </div>
  );
};

export default Phases;
